import { useLazyQuery } from "@apollo/client";
import {
  Badge,
  Box,
  Button,
  Flex,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

import React, { useEffect, useCallback, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import HeaderWithActions from "../../components/HeaderWithActions";
import ListView from "../../components/ListView";
import Meta from "../../components/Meta";
import { useSchemaQuery } from "../../react-apollo-introspection";
import debounce from "lodash.debounce";
import { DELETE, FETCH_ALL, CUSTOM_FETCH_ALL } from "./graphql";
import { setup } from "./setup";
import { AiOutlineHeart, AiOutlineEye } from "react-icons/ai";
import { useRecoilState } from 'recoil';
import { tableAtom } from '../../components/ListView/atoms';

const columns = [
  {
    Header: "",
    accessor: "_id",
    disableSortBy: true,
    Cell: ({ row }) => (
      <Link to={`/${setup.model}/edit/${row.original[setup.accessor]}`}>
        <Button variant="link">View</Button>
      </Link>
    ),
  },

  {
    Header: "Name",
    accessor: "name",
    disableSortBy: false,
    Cell: ({ value }) => <div>{value}</div>,
  },
  {
    Header: "Tracking",
    accessor: "tracking",
    disableSortBy: false,
    Cell: ({ value }) => (
      <Flex direction={"column"}>
        <Flex>
          <AiOutlineEye fontSize={"16px"} />
          <Text as="b">{value?.viewed?.count}</Text>
        </Flex>
        <Flex>
          <AiOutlineHeart fontSize={"16px"} />
          <Text as="b">{value?.liked?.count}</Text>
        </Flex>
      </Flex>
    ),
  },
  {
    Header: "Address",
    accessor: "location",
    disableSortBy: false,
    Cell: ({ value }) => <div> {value?.googleplaces?.formatted_address} </div>,
  },

  {
    Header: "Category",
    accessor: "categories",
    disableSortBy: false,
    Cell: ({ value, row }) => {
      return (
        <Flex gap={5}>
          {value?.eat && (
            <Text
              fontWeight="bold"
              bg="#b52828"
              px="10px"
              py="0.1em"
              color="white"
              borderRadius="50px"
            >
              Eat
            </Text>
          )}
          {value?.sleep && (
            <Text
              fontWeight="bold"
              bg="#749079"
              px="10px"
              py="0.1em"
              color="white"
              borderRadius="50px"
            >
              Sleep
            </Text>
          )}
          {value?.drink && (
            <Text
              fontWeight="bold"
              bg="#6d2db7"
              px="10px"
              py="0.1em"
              color="white"
              borderRadius="50px"
            >
              Drink
            </Text>
          )}
          {value?.daysOut && (
            <Text
              fontWeight="bold"
              bg="#F4A259"
              px="10px"
              py="0.1em"
              color="white"
              borderRadius="50px"
            >
              Days Out
            </Text>
          )}
          {value?.experience && (
            <Text
              fontWeight="bold"
              bg="#3B8EA5"
              px="10px"
              py="0.1em"
              color="white"
              borderRadius="50px"
            >
              Experience
            </Text>
          )}
        </Flex>
      );
    },
  },
];

const filters = [
  {
    value: "last_name",
    type: "text",
    label: "Last Name",
    isOperator: false,
  },
];

const List = ({ title }) => {
  const [fetchData, { loading, error, data }] = useLazyQuery(CUSTOM_FETCH_ALL);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchString, setSearchString] = useState(searchParams.get("searchFilter") || "");
  const [tableState, setTableState] = useRecoilState(tableAtom);
 
    const updateFiltersSafely = (updateFn) => {
      setTableState((old) => ({ ...old, hydrated: "" }));
      searchParams.set("page", "1");
      searchParams.set("limit", "20");
      setTimeout(()=>{
        updateFn();
        setTableState((old) => ({
          ...old,
          params: {
            ...old.params,
            page: 1,
            skip: 0,
            pagination: 20,
          },
          hydrated: setup.model,
        }))}, 0);
    };
 
   const delayedSetSearchParams = (params) => {
      setTimeout(() => setSearchParams(params), 0);
    };


  const confirm = (val) => {
    updateFiltersSafely(() => {
      setSearchString(val);
       if (val) {
        searchParams.set("searchFilter", val);
      } else {
        searchParams.delete("searchFilter");
      }
      delayedSetSearchParams(searchParams);
    });
  };

  const buildFetchFilters = () => {
    const regexp = {};
    if (searchString) regexp.name = searchString;
    return { regexp };
  };


  const _searchFunction = useCallback(debounce(confirm, 500), [searchString]);

  const fetchFilters = useMemo(() => {
    return buildFetchFilters();
  }, [searchString]);

  return (
    <Meta title={setup.title}>
      <HeaderWithActions title={setup.title}>
        <Stack ml="auto" isInline spacing="15px">
          {setup.canAddNew && (
            <Link to={`/${setup.model}/create`}>
              <Button>Add {setup.singular}</Button>
            </Link>
          )}
        </Stack>
      </HeaderWithActions>
      <Box
        mt="25px"
        bg={useColorModeValue("white", "gray.700")}
        shadow="base"
        rounded={{
          base: "md",
          sm: "xl",
        }}
        w="100%"
      >
        <ListView
          setup={setup}
          fetchData={fetchData}
          data={data?.[FETCH_ALL]}
          loading={loading}
          error={error}
          gqlDelete={DELETE}
          columns={columns}
          fetchFilters={fetchFilters}
          // fetchFilters={{ filter: { stripe: { sub_type: "free" } } }}
          filters={filters}
          setExternalSearch={_searchFunction}
        />
      </Box>
    </Meta>
  );
};

export default List;
